/*
 * Copyright (c) 2026 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import * as Sentry from '@sentry/nextjs'
import React, { useEffect } from 'react'

import './globals.css'

import { matterFont } from '@/app/matter-font'
import { Button } from '@/components/ui/Button'

/** A description of the available properties for this error page. */
type GlobalErrorPageProps = {
  /** The error that triggered this page. */
  error: Error & { digest?: string }

  /** Reset handler to trigger a re-render. */
  reset: () => void
}

/**
 * The last-resort error page, for failures in the root layout itself.
 *
 * A route's `error.tsx` cannot catch those, because the layout it would render inside is the thing that failed. This
 * replaces the whole document instead, which is why it renders its own `<html>` and imports the stylesheet directly.
 *
 * For the same reason it cannot use `useTranslations` or the regional `Link`: both need providers that live in the
 * layout that has just failed, and reaching for either here would throw a second error inside the error page. The
 * copy is therefore English-only and the link is a plain anchor — a deliberate trade for a page that should almost
 * never render, and must not be able to fail.
 */
const GlobalErrorPage: React.FC<GlobalErrorPageProps> = ({ error, reset }) => {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang={'en'}>
      <body className={`${matterFont.className} antialiased`}>
        <main className={'grid min-h-screen place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8'}>
          <div className={'text-center'}>
            <p className={'text-base font-semibold text-primary-500'}>Whoops!</p>
            <h1 className={'mt-4 text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-7xl'}>
              Something Went Wrong
            </h1>
            <p className={'mt-6 text-pretty text-lg font-medium text-gray-500 sm:text-xl/8'}>
              It looks like there was an unexpected error.
            </p>
            <p className={'mt-6 text-pretty text-sm font-medium text-gray-500 sm:text-md'}>
              The team has been informed. Please try again or go back home.
            </p>
            <div className={'mt-10 flex items-center justify-center gap-x-6'}>
              <Button variant={'primary'} onClick={() => reset()}>
                Try again
              </Button>
              <a href={'/'} className={'text-sm font-semibold text-gray-900'}>
                Go back home <span aria-hidden={'true'}>&rarr;</span>
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  )
}

export default GlobalErrorPage
